"use client";

import { useEffect, useState } from "react";
import {
  type SessionTier,
  isPremiumGodMode,
  sessionTierFromPlan,
} from "./tiers";
import { isSupabaseConfigured } from "@/lib/supabase/config";

const TIER_STORAGE_KEY = "mindaloo_tier_override";

export interface MindalooSession {
  tier: SessionTier;
  isPremiumGodMode: boolean;
  systemTelemetry: string | null;
  loading: boolean;
  localOnlyMode: boolean;
}

export function useMindalooSession(): MindalooSession {
  const localOnlyMode = !isSupabaseConfigured();
  const [tier, setTier] = useState<SessionTier>("core");
  const [loading, setLoading] = useState(!localOnlyMode);

  useEffect(() => {
    const load = async () => {
      try {
        const override = localStorage.getItem(TIER_STORAGE_KEY) as SessionTier | null;
        if (override && ["core", "starter", "pro", "sovereign", "god_mode"].includes(override)) {
          setTier(override);
          setLoading(false);
          return;
        }

        if (localOnlyMode) {
          setTier("core");
          setLoading(false);
          return;
        }

        const res = await fetch("/api/user/tier");
        if (res.ok) {
          const data = (await res.json()) as { tier?: SessionTier; plan?: string };
          if (data.tier) {
            setTier(data.tier);
          } else if (data.plan) {
            setTier(sessionTierFromPlan(data.plan));
          }
        }
      } catch {
        // Default core tier
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [localOnlyMode]);

  const godMode = isPremiumGodMode(tier);

  return {
    tier,
    isPremiumGodMode: godMode,
    systemTelemetry: godMode
      ? "SYSTEM RUNTIME: MAX_AUTONOMY // MOUNTED NODE: KARMA_AC_CORE_REMOTE"
      : null,
    loading,
    localOnlyMode,
  };
}
